import { ArrowLeftRight, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { RowActions } from "@/components/shared/row-actions";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useActiveBuInstances } from "@/hooks/use-bu-instances";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useSyncMasterDataMappingDrafts } from "@/hooks/use-master-data-extracted-values";
import {
  type MasterDataMapping,
  useDeleteMasterDataMapping,
  useMasterDataMappings,
} from "@/hooks/use-master-data-mappings";
import { useSession } from "@/lib/auth-client";
import type { MasterDataEntityType } from "@/lib/enums";
import { MasterDataMappingCreateDialog } from "./MasterDataMappingCreateDialog";
import { MasterDataMappingEditDialog } from "./MasterDataMappingEditDialog";

interface MasterDataMappingsTableProps {
  /** Fixed by the active tab — see MasterDataMappingsLayout. */
  entityType: MasterDataEntityType;
}

/**
 * The master-data mapping admin grid, scoped to one entity type (the active
 * tab). Read-only for non-admins (search/filter/sort/paginate still work) —
 * "New mapping", row Edit/Delete, and "Sync new values" are admin-only, both
 * here and on the backing routes (see `.claude/rules` / `src/index.ts`).
 */
export function MasterDataMappingsTable({ entityType }: MasterDataMappingsTableProps) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";

  const grid = useDataGrid({ defaultSort: "createdAt" });
  const [sourceBuFilter, setSourceBuFilter] = useState("");
  const [targetBuFilter, setTargetBuFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterDataMapping | null>(null);

  const { data: activeBuInstances = [] } = useActiveBuInstances();
  const buFilterOptions = activeBuInstances.map((bu) => ({ value: bu.id, label: bu.name }));

  const { data, isLoading, isError, error, refetch } = useMasterDataMappings(
    grid.query,
    grid.pageRequest,
    {
      entityType,
      sourceBuId: sourceBuFilter || undefined,
      targetBuId: targetBuFilter || undefined,
    },
  );
  const deleteMapping = useDeleteMasterDataMapping();
  const syncDrafts = useSyncMasterDataMappingDrafts();

  const mappings = data?.data ?? [];

  function handleSourceBuFilterChange(value: string) {
    setSourceBuFilter(value);
    grid.resetToFirstPage();
  }

  function handleTargetBuFilterChange(value: string) {
    setTargetBuFilter(value);
    grid.resetToFirstPage();
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteMapping.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  function handleSyncDrafts() {
    if (!sourceBuFilter || !targetBuFilter) return;
    syncDrafts.mutate({ sourceBuId: sourceBuFilter, targetBuId: targetBuFilter, entityType });
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search reference IDs..."
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={sourceBuFilter}
        onValueChange={handleSourceBuFilterChange}
        options={buFilterOptions}
        allOption={{ label: "Any source BU" }}
        placeholder="Any source BU"
        className="w-[180px]"
        aria-label="Filter by source BU"
      />
      <EnumSelect
        value={targetBuFilter}
        onValueChange={handleTargetBuFilterChange}
        options={buFilterOptions}
        allOption={{ label: "Any target BU" }}
        placeholder="Any target BU"
        className="w-[180px]"
        aria-label="Filter by target BU"
      />
      {isAdmin && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={!sourceBuFilter || !targetBuFilter || syncDrafts.isPending}
            onClick={handleSyncDrafts}
            title={
              !sourceBuFilter || !targetBuFilter
                ? "Pick a source and target BU first"
                : "Add draft rows for any newly-seen source values"
            }
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            {syncDrafts.isPending ? "Syncing…" : "Sync new values"}
          </Button>
          <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New mapping
          </Button>
        </>
      )}
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load master data mappings"}
          onRetry={() => refetch()}
        />
        {isAdmin && (
          <MasterDataMappingCreateDialog
            entityType={entityType}
            open={createOpen}
            onOpenChange={setCreateOpen}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "Source BU" },
          { label: "Source ref", sortKey: "sourceRefId" },
          { label: "Target BU" },
          { label: "Target ref", sortKey: "targetRefId" },
          { label: "Status", sortKey: "isActive" },
          { label: "", className: "w-12" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ArrowLeftRight}
            title="No mappings found"
            description="Try adjusting your search or filters."
          />
        }
      >
        {mappings.map((mapping) => (
          <tr
            key={mapping.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 text-muted-foreground">{mapping.sourceBuName ?? "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{mapping.sourceRefId}</td>
            <td className="px-4 py-3 text-muted-foreground">{mapping.targetBuName ?? "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">
              {mapping.targetRefId ?? (
                <Badge variant="warning" className="font-sans text-xs font-normal">
                  Needs target
                </Badge>
              )}
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={mapping.isActive ? "active" : "inactive"} />
            </td>
            <td className="px-4 py-3 text-right">
              {isAdmin && (
                <RowActions
                  actions={[
                    {
                      label: "Edit",
                      icon: Pencil,
                      onSelect: () => setEditId(mapping.id),
                    },
                    {
                      label: "Delete",
                      icon: Trash2,
                      destructive: true,
                      onSelect: () => setDeleteTarget(mapping),
                    },
                  ]}
                />
              )}
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="mappings"
      />

      {isAdmin && (
        <>
          <MasterDataMappingCreateDialog
            entityType={entityType}
            open={createOpen}
            onOpenChange={setCreateOpen}
          />
          <MasterDataMappingEditDialog
            id={editId}
            onOpenChange={(open) => !open && setEditId(null)}
          />
          <ConfirmDialog
            open={deleteTarget !== null}
            onOpenChange={(open) => {
              if (!open) setDeleteTarget(null);
            }}
            title="Delete mapping"
            description={`Delete this mapping (${deleteTarget?.sourceRefId} → ${deleteTarget?.targetRefId ?? "—"})? This cannot be undone.`}
            confirmLabel="Delete"
            variant="destructive"
            loading={deleteMapping.isPending}
            onConfirm={handleDeleteConfirm}
          />
        </>
      )}
    </div>
  );
}
